import { IOrderFill } from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { isFixEditLocked } from "@/lib/businessRules/fixEditLockRule";
import tradeDataStore from "@/store/tradeDataStore";

export const FIX_LOCKED_FILL_REASON =
  "Editing Fills is not allowed for this Counterparty";

/**
 * Fill State Helper Functions
 *
 * These helpers determine the state of a fill based on its fillType field:
 * - fillType: "New" - New fill created by frontend, not yet sent to backend
 * - fillType: "Updated" - User clicked Edit on a locked fill, ready to be updated
 * - fillType: "Cancel" - User clicked Remove on an existing fill, will be cancelled
 * - fillType: "Unchanged" - Fill exists but no changes made
 *
 * The filledAt field is preserved as the timestamp (ISO string from backend).
 * A fill is "locked" when it has a filledAt timestamp and fillType is "Unchanged".
 */

/**
 * Checks if a fill is in cancelled state.
 * Cancelled fills should be displayed with strikethrough and excluded from totals.
 */
export function isFillCancelled(fill: IOrderFill): boolean {
  return fill.fillType === "Cancel";
}

/**
 * Checks if a fill is locked (has a timestamp and is unchanged).
 * Locked fills cannot be edited until the user clicks the Edit button.
 * A fill is locked when it has a filledAt timestamp AND fillType is "Unchanged" or undefined.
 */
export function isFillLocked(fill: IOrderFill): boolean {
  // No timestamp means not locked (could be a new fill)
  if (!fill.filledAt) return false;

  // If fillType indicates pending changes, it's not locked
  if (
    fill.fillType === "New" ||
    fill.fillType === "Updated" ||
    fill.fillType === "Cancel"
  ) {
    return false;
  }

  // Has timestamp and is "Unchanged" or undefined = locked
  return true;
}

// LQC-3241 - the RP's own customer codes say nothing here, so the OP's decide.
export function isFillFixEditLocked(
  trade: ExecutionStrategy | undefined,
  blockId: number | undefined,
): boolean {
  const leg = trade?.legs?.find((l) => l.blockId === blockId);
  return isFixEditLocked(leg?.source, [trade?.originatingCounterparty]);
}

/**
 * Checks if a fill's price and quantity cells should be editable.
 * Editable when: fillType is "New" or "Updated"
 * Not editable when: cancelled, locked (has timestamp + Unchanged), or undefined fillType with timestamp
 */
export function isFillCellsEditable(fill: IOrderFill): boolean {
  return fill.fillType === "New" || fill.fillType === "Updated";
}

/**
 * Checks if a fill is a new fill created by the frontend.
 * New fills can be completely removed from the data when deleted.
 */
export function isFillNew(fill: IOrderFill): boolean {
  return fill.fillType === "New";
}

/**
 * Checks if a fill was received from the backend.
 * Backend fills have fillType of undefined, "Unchanged", "Updated", or "Cancel".
 * Only frontend-created fills ("New") are NOT backend fills.
 */
export function isFillFromBackend(fill: IOrderFill): boolean {
  return (
    fill.fillType === undefined ||
    fill.fillType === "Unchanged" ||
    fill.fillType === "Updated" ||
    fill.fillType === "Cancel"
  );
}

/**
 * Checks if a trade has any fills received from the backend.
 * Used to gate the fill-dependent actions in isActionAllowedByFills.
 * @param data - Trade data: RP fills are on legs, OP fills on executionRequestCounterParties > legs
 * @returns true if any leg has at least one fill from the backend
 */
export function hasBackendFills(data: any): boolean {
  const opLegs = (data?.executionRequestCounterParties ?? []).flatMap(
    (cp: any) => cp.legs ?? [],
  );
  const legs = [...(data?.legs ?? []), ...opLegs];

  return legs.some(
    (leg: any) => Array.isArray(leg.fills) && leg.fills.some(isFillFromBackend),
  );
}

/**
 * Hides menu actions that a trade's fills rule out.
 * - "No More Fills" needs a fill to close out.
 * - "Not Dealt" is refused by the API once an Order has a fill (LQC-3248).
 * - "Recall" is blocked once an Order has a fill (LQC-3250).
 * - "Reject" is hidden from the RP until every fill is deleted (LQC-3251).
 */
export function isActionAllowedByFills(actionId: string, data: any): boolean {
  if (actionId === "No More Fills") return hasBackendFills(data);
  if (
    actionId === "Not Dealt" ||
    actionId === "Recall" ||
    actionId === "Reject" // Will be an RP action
  )
    return !hasBackendFills(data);
  return true;
}

/**
 * Gets the fill from the store by esid, blockId, and fillId.
 * Searches both RP and OP trade structures.
 *
 * When extFillId is provided, it acts as a fallback: if the fillId lookup
 * fails (stale after backend assigns a new fillId), tries matching by extFillId instead.
 */
export function getFillFromStore(
  tradeData: any[],
  esid: string,
  blockId: number,
  fillId: string | number,
  extFillId?: number,
): IOrderFill | null {
  const trade = tradeData.find((t) => t.esid === esid);
  if (!trade) return null;

  // For RP trades, fills are in the legs array
  if (trade.role === "RP" && trade.legs) {
    const leg = trade.legs.find((l: any) => l.blockId === blockId);
    if (leg?.fills) {
      const fill = leg.fills.find((f: IOrderFill) => f.fillId === fillId);
      if (fill) return fill;
      // Fallback: fillId is stale after backend update, try extFillId
      if (extFillId != null) {
        return (
          leg.fills.find((f: IOrderFill) => f.extFillId === extFillId) ?? null
        );
      }
      return null;
    }
  }

  // For OP trades, fills are in executionRequestCounterParties > legs
  if (trade.role === "OP" && trade.executionRequestCounterParties) {
    for (const cp of trade.executionRequestCounterParties) {
      if (cp.legs) {
        const leg = cp.legs.find((l: any) => l.blockId === blockId);
        if (leg?.fills) {
          const fill = leg.fills.find((f: IOrderFill) => f.fillId === fillId);
          if (fill) return fill;
          // Fallback: fillId is stale after backend update, try extFillId
          if (extFillId != null) {
            const fallbackFill = leg.fills.find(
              (f: IOrderFill) => f.extFillId === extFillId,
            );
            if (fallbackFill) return fallbackFill;
          }
        }
      }
    }
  }

  return null;
}

/**
 * Unlocks a fill by setting its fillType to "Updated".
 * This allows the user to edit the fill's price and quantity.
 * The filledAt timestamp is preserved.
 *
 * When extFillId is provided, it acts as a fallback: if the fillId doesn't
 * match any fill (stale after backend update), tries matching by extFillId.
 * Returns the resolved fillId so the caller can update stale references.
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID
 * @param fillId - The fill ID to unlock
 * @param extFillId - Optional fallback extFillId when fillId is stale
 * @returns The resolved fillId (may differ from input if extFillId fallback was used), or undefined if not found
 */
export function unlockFill(
  esid: string,
  blockId: number,
  fillId: string | number,
  extFillId?: number,
): string | number | undefined {
  const tradeData = tradeDataStore.getState().tradeData;
  const tradeIndex = tradeData.findIndex((t) => t.esid === esid);

  if (tradeIndex === -1) return undefined;

  const trade = tradeData[tradeIndex];
  let resolvedFillId: string | number | undefined = undefined;

  // Helper to match fill by fillId, falling back to extFillId
  const matchFill = (fill: IOrderFill): boolean => {
    if (fill.fillId === fillId) return true;
    if (extFillId != null && fill.extFillId === extFillId) return true;
    return false;
  };

  // Handle RP trades - fills are in trade.legs
  if (trade.role === "RP" && trade.legs) {
    const newLegs = trade.legs.map((leg: any) => {
      if (leg.blockId !== blockId) return leg;

      const newFills = (leg.fills ?? []).map((fill: IOrderFill) => {
        if (!matchFill(fill)) return fill;
        resolvedFillId = fill.fillId;
        return {
          ...fill,
          fillType: "Updated" as const,
          // Preserve historic values if not already set
          historicFillRate:
            fill.historicFillRate !== undefined
              ? fill.historicFillRate
              : fill.fillRate,
          historicFillSize:
            fill.historicFillSize !== undefined
              ? fill.historicFillSize
              : fill.fillSize,
        };
      });

      return { ...leg, fills: newFills };
    });

    const newTrade = { ...trade, legs: newLegs };
    const newTradeData = tradeData.map((t, i) =>
      i === tradeIndex ? newTrade : t,
    );

    tradeDataStore.getState().updateTradeData(newTradeData);
  }

  // Handle OP trades - fills are in executionRequestCounterParties > legs
  if (trade.role === "OP" && trade.executionRequestCounterParties) {
    const newCPs = trade.executionRequestCounterParties.map((cp: any) => {
      if (!cp.legs) return cp;

      const newLegs = cp.legs.map((leg: any) => {
        if (leg.blockId !== blockId) return leg;

        const newFills = (leg.fills ?? []).map((fill: IOrderFill) => {
          if (!matchFill(fill)) return fill;
          resolvedFillId = fill.fillId;
          return {
            ...fill,
            fillType: "Updated" as const,
            // Preserve historic values if not already set
            historicFillRate:
              fill.historicFillRate !== undefined
                ? fill.historicFillRate
                : fill.fillRate,
            historicFillSize:
              fill.historicFillSize !== undefined
                ? fill.historicFillSize
                : fill.fillSize,
          };
        });

        return { ...leg, fills: newFills };
      });

      return { ...cp, legs: newLegs };
    });

    const newTrade = { ...trade, executionRequestCounterParties: newCPs };
    const newTradeData = tradeData.map((t, i) =>
      i === tradeIndex ? newTrade : t,
    );

    tradeDataStore.getState().updateTradeData(newTradeData);
  }

  return resolvedFillId;
}
